import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import type {
  AttachmentRow,
  MessagingAttachmentSource,
} from '../../../api/attachments'
import type { MessagingMessageCreateKind } from '../../../api/generated/model/messagingMessageCreateKind'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import { useMessagingUiStore } from '../../../store/messaging-ui'
import { PexIcon } from '../../../components/icons/PexIcon'
import type { MessagingSurface } from './MessageBubble'
import { MessagingAttachmentPicker } from './MessagingAttachmentPicker'
import { MessagingPhotoPicker } from './MessagingPhotoPicker'
import {
  MessagingVoiceRecorder,
  type MessagingVoiceRecorderHandle,
  type VoiceRecorderControlState,
} from './MessagingVoiceRecorder'
import { MessagingVoiceLeaveGuardContext } from './useMessagingVoiceRecorder'
import {
  canSendMessagingDraft,
  classifyAttachment,
  validateOutgoingMedia,
} from './messaging-media'

type MessageComposerProps = {
  appearance?: 'default' | 'messenger' | 'admin-desk'
  surface: MessagingSurface
  conversationId: string
  destination: string
  canSend: boolean
  canUseInternalNotes: boolean
  showInternalNotes?: boolean
  disabledReason?: string
  attachmentSource?: MessagingAttachmentSource
  autoFocus?: boolean
  onSend: (message: {
    body: string
    kind: MessagingMessageCreateKind
    attachment_ids: string[]
  }) => void | Promise<void>
}

const IDLE_VOICE_CONTROL: VoiceRecorderControlState = {
  phase: 'idle',
  canSubmit: false,
  canActivate: true,
  elapsedSeconds: 0,
}

export function MessageComposer({
  appearance = 'default',
  surface,
  conversationId,
  destination,
  canSend,
  canUseInternalNotes,
  showInternalNotes = canUseInternalNotes,
  disabledReason,
  attachmentSource,
  autoFocus = false,
  onSend,
}: MessageComposerProps) {
  const t = useT(messagingI18n)
  const voiceLeaveGuard = useContext(MessagingVoiceLeaveGuardContext)
  const voiceBlocksOtherId = useId()
  const otherBlocksVoiceId = useId()
  const driveTriggerRef = useRef<HTMLButtonElement>(null)
  const photoTriggerRef = useRef<HTMLButtonElement>(null)
  const voiceTriggerRef = useRef<HTMLButtonElement>(null)
  const voiceRecorderRef = useRef<MessagingVoiceRecorderHandle>(null)
  const voiceSubmitRef = useRef<Promise<boolean> | null>(null)
  const draft = useMessagingUiStore(
    (state) => state.getDraft(surface, conversationId),
  )
  const setDraft = useMessagingUiStore((state) => state.setDraft)
  const clearDraft = useMessagingUiStore((state) => state.clearDraft)
  const [kind, setKind] = useState<MessagingMessageCreateKind>('text')
  const attachmentKey = `${surface}:${conversationId}`
  const [voiceControlSnapshot, setVoiceControlSnapshot] = useState<{
    key: string
    state: VoiceRecorderControlState
  }>({ key: attachmentKey, state: IDLE_VOICE_CONTROL })
  const voiceControl =
    voiceControlSnapshot.key === attachmentKey
      ? voiceControlSnapshot.state
      : IDLE_VOICE_CONTROL
  const updateVoiceControl = useCallback(
    (state: VoiceRecorderControlState) =>
      setVoiceControlSnapshot({ key: attachmentKey, state }),
    [attachmentKey],
  )
  const [voiceDiscardConfirmation, setVoiceDiscardConfirmation] = useState({
    key: attachmentKey,
    open: false,
  })
  const confirmVoiceDiscard =
    voiceDiscardConfirmation.key === attachmentKey &&
    voiceDiscardConfirmation.open
  const setConfirmVoiceDiscard = useCallback(
    (open: boolean) =>
      setVoiceDiscardConfirmation({ key: attachmentKey, open }),
    [attachmentKey],
  )
  const [attachmentSelection, setAttachmentSelection] = useState({
    key: attachmentKey,
    ids: [] as string[],
  })
  const attachmentIds =
    attachmentSelection.key === attachmentKey ? attachmentSelection.ids : []
  const updateAttachmentIds = useCallback(
    (update: (ids: string[]) => string[]) => {
      setAttachmentSelection((current) => {
        const ids = current.key === attachmentKey ? current.ids : []
        return {
          key: attachmentKey,
          ids: [...new Set(update(ids))],
        }
      })
    },
    [attachmentKey],
  )
  const setAttachmentIds = (ids: string[]) =>
    updateAttachmentIds(() => ids)
  const [uploadedSelection, setUploadedSelection] = useState<{
    key: string
    rows: AttachmentRow[]
  }>({ key: attachmentKey, rows: [] })
  const localUploadedRows =
    uploadedSelection.key === attachmentKey ? uploadedSelection.rows : []
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [activePanelState, setActivePanelState] = useState<{
    key: string
    panel: 'drive' | 'photo' | 'voice' | null
  }>({
    key: attachmentKey,
    panel: null,
  })
  const activePanel =
    activePanelState.key === attachmentKey ? activePanelState.panel : null
  const togglePanel = (panel: 'drive' | 'photo' | 'voice') =>
    setActivePanelState({
      key: attachmentKey,
      panel: activePanel === panel ? null : panel,
    })
  const [mediaBusyByKey, setMediaBusyByKey] = useState<
    Record<string, { photo: boolean; voice: boolean }>
  >({})
  const currentMediaBusy = mediaBusyByKey[attachmentKey] ?? {
    photo: false,
    voice: false,
  }
  const setPhotoBusy = useCallback(
    (busy: boolean) =>
      setMediaBusyByKey((current) => ({
        ...current,
        [attachmentKey]: {
          photo: busy,
          voice: current[attachmentKey]?.voice ?? false,
        },
      })),
    [attachmentKey],
  )
  const setVoiceBusy = useCallback(
    (busy: boolean) =>
      setMediaBusyByKey((current) => ({
        ...current,
        [attachmentKey]: {
          photo: current[attachmentKey]?.photo ?? false,
          voice: busy,
        },
      })),
    [attachmentKey],
  )
  const mediaBusy = currentMediaBusy.photo || currentMediaBusy.voice
  const [mediaResetState, setMediaResetState] = useState({
    key: attachmentKey,
    version: 0,
  })
  const mediaResetVersion =
    mediaResetState.key === attachmentKey ? mediaResetState.version : 0
  const mediaChildKey = `${attachmentKey}:${mediaResetVersion}`
  const selectedRowsById = new Map(
    [...(attachmentSource?.rows ?? []), ...localUploadedRows].map((row) => [
      row.id,
      row,
    ]),
  )
  const selectedAttachmentRows = attachmentIds.flatMap((id) => {
    const row = selectedRowsById.get(id)
    return row ? [row] : []
  })
  const outgoingMedia = attachmentIds.map((id) => ({
    id,
    kind: classifyAttachment(selectedRowsById.get(id) ?? { content_type: '' }),
  }))
  const mediaValidation = validateOutgoingMedia(outgoingMedia)
  const selectedVoice = selectedAttachmentRows.find(
    (row) => classifyAttachment(row) === 'voice',
  )
  const hasOtherAttachments = attachmentIds.some(
    (id) => id !== selectedVoice?.id,
  )
  const otherAttachmentLabels = selectedAttachmentRows
    .filter((row) => row.id !== selectedVoice?.id)
    .map((row) => row.original_filename)
  const canSubmitText =
    canSend &&
    (kind !== 'internal_note' || canUseInternalNotes) &&
    mediaValidation.ok &&
    canSendMessagingDraft(draft, attachmentIds, mediaBusy || isSubmitting)
  const canSubmitVoice =
    voiceControl.canSubmit &&
    canSend &&
    kind !== 'internal_note' &&
    !isSubmitting &&
    !currentMediaBusy.photo &&
    !hasOtherAttachments
  const canSubmit = canSubmitVoice || canSubmitText
  const voiceCaptureActive =
    voiceControl.phase === 'requesting' ||
    voiceControl.phase === 'recording' ||
    voiceControl.phase === 'preview' ||
    voiceControl.phase === 'uploading' ||
    voiceControl.phase === 'failed'

  const onMediaUploaded = useCallback(
    (row: AttachmentRow) => {
      setUploadedSelection((current) => {
        const rows = current.key === attachmentKey ? current.rows : []
        return {
          key: attachmentKey,
          rows: [...rows.filter((candidate) => candidate.id !== row.id), row],
        }
      })
      updateAttachmentIds((ids) => [...ids, row.id])
    },
    [attachmentKey, setUploadedSelection, updateAttachmentIds],
  )

  const removeConfirmedMedia = useCallback(
    (id: string) => {
      updateAttachmentIds((ids) => ids.filter((candidate) => candidate !== id))
      setUploadedSelection((current) =>
        current.key === attachmentKey
          ? {
              ...current,
              rows: current.rows.filter((row) => row.id !== id),
            }
          : current,
      )
    },
    [attachmentKey, setUploadedSelection, updateAttachmentIds],
  )

  const submit = async () => {
    const body = draft.trim()
    if (voiceControl.canSubmit) {
      if (!canSubmitVoice || voiceSubmitRef.current) return
      const operation =
        voiceRecorderRef.current?.submitPreview() ?? Promise.resolve(false)
      voiceSubmitRef.current = operation
      try {
        await operation
      } finally {
        if (voiceSubmitRef.current === operation) voiceSubmitRef.current = null
      }
      return
    }
    if (
      !canSubmitText ||
      submittingRef.current
    ) return

    submittingRef.current = true
    setIsSubmitting(true)
    try {
      await onSend({ body, kind, attachment_ids: attachmentIds })
      clearDraft(surface, conversationId)
      updateAttachmentIds(() => [])
      setUploadedSelection({ key: attachmentKey, rows: [] })
      setActivePanelState({ key: attachmentKey, panel: null })
      setMediaResetState((current) => ({
        key: attachmentKey,
        version: current.key === attachmentKey ? current.version + 1 : 1,
      }))
      if (kind === 'internal_note') setKind('text')
    } catch {
      // A failed send keeps the exact draft for retry or editing.
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const sendVoice = async (row: AttachmentRow) => {
    if (submittingRef.current) throw new Error('Message submission is already in progress.')
    submittingRef.current = true
    setIsSubmitting(true)
    try {
      await onSend({ body: draft.trim(), kind, attachment_ids: [row.id] })
      clearDraft(surface, conversationId)
      updateAttachmentIds(() => [])
      setUploadedSelection({ key: attachmentKey, rows: [] })
      setActivePanelState({ key: attachmentKey, panel: null })
      setMediaResetState((current) => ({ key: attachmentKey, version: current.key === attachmentKey ? current.version + 1 : 1 }))
      if (kind === 'internal_note') setKind('text')
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return
    if (event.nativeEvent.isComposing || event.keyCode === 229) return
    event.preventDefault()
    void submit()
  }

  const onComposerKeyDownCapture = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !activePanel) return
    event.preventDefault()
    event.stopPropagation()
    if (mediaBusy) return

    const trigger =
      activePanel === 'drive'
        ? driveTriggerRef.current
        : activePanel === 'photo'
          ? photoTriggerRef.current
          : voiceTriggerRef.current
    setActivePanelState({ key: attachmentKey, panel: null })
    trigger?.focus()
  }

  const activateVoice = () => {
    setActivePanelState({ key: attachmentKey, panel: 'voice' })
    voiceRecorderRef.current?.activate()
  }

  const requestInternalNote = () => {
    if (kind === 'internal_note') {
      setKind('text')
      return
    }
    if (voiceCaptureActive || Boolean(selectedVoice)) {
      setConfirmVoiceDiscard(true)
      return
    }
    setKind('internal_note')
  }

  const discardVoiceAndSwitchToInternalNote = () => {
    voiceRecorderRef.current?.discard()
    setConfirmVoiceDiscard(false)
    setActivePanelState({ key: attachmentKey, panel: null })
    setKind('internal_note')
  }

  const discardVoiceForLeave = useCallback(() => {
    voiceRecorderRef.current?.discard()
    setConfirmVoiceDiscard(false)
    setActivePanelState({ key: attachmentKey, panel: null })
  }, [attachmentKey, setConfirmVoiceDiscard])

  useEffect(
    () =>
      voiceLeaveGuard?.register(
        conversationId,
        voiceCaptureActive || Boolean(selectedVoice),
        discardVoiceForLeave,
      ),
    [
      conversationId,
      discardVoiceForLeave,
      selectedVoice,
      voiceCaptureActive,
      voiceLeaveGuard,
    ],
  )

  const mediaControlDisabled = !canSend || isSubmitting || mediaBusy
  const otherMediaControlDisabled =
    mediaControlDisabled || voiceCaptureActive || Boolean(selectedVoice)
  const voiceOwnActive =
    voiceControl.phase === 'requesting' || voiceControl.phase === 'recording'
  const voiceControlDisabled =
    !canSend ||
    kind === 'internal_note' ||
    isSubmitting ||
    currentMediaBusy.photo ||
    (!voiceOwnActive && currentMediaBusy.voice) ||
    (!selectedVoice && hasOtherAttachments) ||
    !voiceControl.canActivate
  const voiceControlLabel =
    voiceControl.phase === 'recording'
      ? t.media.stopRecording
      : voiceControl.phase === 'requesting'
        ? t.media.cancelRecording
        : t.media.recordVoiceMessage
  const voiceControlPressed = voiceOwnActive
  let activeMediaPanel: ReactNode = null
  if (attachmentSource && activePanel === 'drive') {
    activeMediaPanel = (
      <MessagingAttachmentPicker
        appearance={appearance === 'admin-desk' ? 'default' : appearance}
        source={attachmentSource}
        selectedIds={attachmentIds}
        onSelectionChange={setAttachmentIds}
        disabled={otherMediaControlDisabled}
      />
    )
  } else if (attachmentSource && activePanel === 'photo') {
    activeMediaPanel = (
      <MessagingPhotoPicker
        key={`photo:${mediaChildKey}`}
        appearance={appearance === 'admin-desk' ? 'default' : appearance}
        source={attachmentSource}
        selectedRows={selectedAttachmentRows}
        disabled={otherMediaControlDisabled}
        onUploaded={onMediaUploaded}
        onRemoveConfirmed={removeConfirmedMedia}
        onBusyChange={setPhotoBusy}
      />
    )
  }
  const voiceRecorderPanel = attachmentSource && kind !== 'internal_note' ? (
    <MessagingVoiceRecorder
      key={`voice:${mediaChildKey}`}
      ref={voiceRecorderRef}
      appearance={appearance}
      source={attachmentSource}
      visible={activePanel === 'voice' || voiceControl.phase !== 'idle'}
      disabled={!canSend || isSubmitting || currentMediaBusy.photo}
      hasOtherAttachments={hasOtherAttachments}
      selectedVoice={selectedVoice}
      onUploaded={onMediaUploaded}
      onRemoveConfirmed={removeConfirmedMedia}
      onBusyChange={setVoiceBusy}
      onControlStateChange={updateVoiceControl}
      onSendVoice={sendVoice}
    />
  ) : null

  if (appearance === 'messenger' && disabledReason) {
    return (
      <section
        className="border-t border-white/10 bg-[#11161d] p-3"
        data-messaging-media-panel-open={activePanel ? 'true' : 'false'}
        onKeyDownCapture={onComposerKeyDownCapture}
      >
        <p
          className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3 text-sm leading-relaxed text-slate-300"
          role="note"
        >
          {disabledReason}
        </p>
      </section>
    )
  }

  if (appearance === 'messenger') {
    return (
      <section
        className="border-t border-white/10 bg-[#11161d] p-3"
        data-messaging-media-panel-open={activePanel ? 'true' : 'false'}
        onKeyDownCapture={onComposerKeyDownCapture}
      >
        {activeMediaPanel ? (
          <div className="mb-2 max-h-60 overflow-y-auto overscroll-contain">
            {activeMediaPanel}
          </div>
        ) : null}
        {voiceRecorderPanel}

        <div className="rounded-2xl border border-white/25 bg-[#171c23] p-2 shadow-[0_8px_24px_rgba(0,0,0,0.18)] focus-within:border-white/50 focus-within:ring-2 focus-within:ring-white/10">
          {selectedAttachmentRows.length > 0 ? (
            <ul className="mb-1.5 flex max-w-full gap-1.5 overflow-x-auto pb-1" aria-label={t.attachments.title}>
              {selectedAttachmentRows.map((row) => {
                const mediaKind = classifyAttachment(row)
                const iconName =
                  mediaKind === 'photo'
                    ? 'image'
                    : mediaKind === 'voice'
                      ? 'microphone'
                      : 'documents'
                return (
                <li key={row.id} className="shrink-0">
                  <button
                    type="button"
                    aria-label={t.attachments.removeFile(row.original_filename)}
                    disabled={!canSend || isSubmitting}
                    onClick={() =>
                      setAttachmentIds(
                        attachmentIds.filter((id) => id !== row.id),
                      )
                    }
                    className="inline-flex min-h-9 max-w-48 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.07] px-2.5 text-xs text-slate-200 transition hover:bg-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:opacity-50"
                  >
                    <PexIcon name={iconName} size="sm" decorative className="text-slate-400" />
                    <span className="truncate">{row.original_filename}</span>
                    <PexIcon name="close" size="xs" decorative className="text-slate-400" />
                  </button>
                </li>
                )
              })}
            </ul>
          ) : null}

          <textarea
            autoFocus={autoFocus}
            aria-label={t.composer.placeholder}
            value={draft}
            rows={2}
            disabled={!canSend || isSubmitting}
            placeholder={t.composer.placeholder}
            className="min-h-14 w-full resize-none bg-transparent px-1.5 py-1 text-sm leading-relaxed text-slate-100 outline-none placeholder:text-slate-500 disabled:cursor-not-allowed"
            onChange={(event) =>
              setDraft(surface, conversationId, event.target.value)
            }
            onKeyDown={onKeyDown}
          />

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-1">
              {attachmentSource ? (
                <>
                  <button
                    ref={driveTriggerRef}
                    type="button"
                    aria-label={t.attachments.title}
                    aria-expanded={activePanel === 'drive'}
                    aria-describedby={
                      selectedVoice ? voiceBlocksOtherId : undefined
                    }
                    disabled={otherMediaControlDisabled}
                    onClick={() => togglePanel('drive')}
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40 ${
                      activePanel === 'drive'
                        ? 'bg-white/15 text-white'
                        : 'text-slate-400 hover:bg-white/[0.08] hover:text-white'
                    }`}
                  >
                    <PexIcon name="link" size="md" decorative className="text-current" />
                  </button>
                  <button
                    ref={photoTriggerRef}
                    type="button"
                    aria-label={t.media.addPhotos}
                    aria-expanded={activePanel === 'photo'}
                    aria-describedby={
                      selectedVoice ? voiceBlocksOtherId : undefined
                    }
                    disabled={otherMediaControlDisabled}
                    onClick={() => togglePanel('photo')}
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40 ${
                      activePanel === 'photo'
                        ? 'bg-white/15 text-white'
                        : 'text-slate-400 hover:bg-white/[0.08] hover:text-white'
                    }`}
                  >
                    <PexIcon name="image" size="md" decorative className="text-current" />
                  </button>
                  <button
                    ref={voiceTriggerRef}
                    type="button"
                    aria-label={voiceControlLabel}
                    aria-expanded={activePanel === 'voice'}
                    aria-pressed={voiceControlPressed}
                    aria-describedby={
                      hasOtherAttachments ? otherBlocksVoiceId : undefined
                    }
                    disabled={voiceControlDisabled}
                    onClick={activateVoice}
                    className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40 ${
                      voiceControlPressed
                        ? 'bg-[var(--color-danger)] text-white'
                        : activePanel === 'voice'
                          ? 'bg-white/15 text-white'
                        : 'text-slate-400 hover:bg-white/[0.08] hover:text-white'
                    }`}
                  >
                    <PexIcon name={voiceControl.phase === 'recording' ? 'stopRecording' : 'microphone'} size="md" decorative className="text-current" />
                  </button>
                </>
              ) : null}
            </div>
            <button
              type="button"
              aria-label={isSubmitting ? t.composer.sending : t.composer.send}
              disabled={!canSubmit}
              onClick={() => void submit()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-action)] text-white transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#171c23] disabled:cursor-not-allowed disabled:opacity-35"
            >
              <PexIcon
                name={isSubmitting ? 'refresh' : 'distribution'}
                size="md"
                decorative
                className={isSubmitting ? 'animate-spin text-current motion-reduce:animate-none' : 'text-current'}
              />
            </button>
          </div>
        </div>
        {selectedVoice ? (
          <p id={voiceBlocksOtherId} className="sr-only">
            {t.media.voiceAttachmentLabel}: {selectedVoice.original_filename}
          </p>
        ) : null}
        {hasOtherAttachments ? (
          <p id={otherBlocksVoiceId} className="sr-only">
            {t.attachments.title}: {otherAttachmentLabels.join(', ')}
          </p>
        ) : null}
      </section>
    )
  }

  return (
    <section
      className={appearance === 'admin-desk' ? 'bg-[var(--color-surface)] p-2.5' : 'border-t border-[var(--color-border)] bg-[var(--color-surface)] p-3 sm:p-4'}
      data-appearance={appearance}
      data-messaging-media-panel-open={activePanel ? 'true' : 'false'}
      onKeyDownCapture={onComposerKeyDownCapture}
    >
      {disabledReason ? (
        <p className="mb-3 rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-text-muted)]" role="note">
          {disabledReason}
        </p>
      ) : null}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-xs text-[var(--color-text-muted)]">
          <p className="font-medium">
          {kind === 'internal_note'
            ? t.composer.internalNoteDestination
            : `${t.composer.replyTo} ${destination}`}
          </p>
          {kind === 'internal_note' ? (
            <p className="mt-1 max-w-2xl leading-relaxed">
              {t.composer.internalNoteSimulation}
            </p>
          ) : null}
        </div>
        {showInternalNotes ? (
          <button
            type="button"
            aria-pressed={kind === 'internal_note'}
            disabled={!canSend || !canUseInternalNotes || isSubmitting}
            className={`rounded-[var(--list-control-radius)] border px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] ${appearance === 'admin-desk' ? 'h-8' : 'min-h-11'} ${
              kind === 'internal_note'
                ? 'border-[var(--color-warning)] bg-[var(--color-surface-muted)] text-[var(--color-text)]'
                : 'border-[var(--list-control-border)] bg-[var(--list-control-bg)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]'
            }`}
            onClick={requestInternalNote}
          >
            {t.composer.internalNote}
          </button>
        ) : null}
      </div>
      {confirmVoiceDiscard ? (
        <div
          className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-panel)] border border-[var(--color-warning)] bg-[var(--color-surface-muted)] px-3 py-2"
          role="alert"
        >
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[var(--color-text)]">
              {t.media.discardVoiceForInternalNote}
            </p>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              {t.media.discardVoiceForInternalNoteDescription}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="min-h-9 rounded-[var(--list-control-radius)] border border-[var(--list-control-border)] bg-[var(--list-control-bg)] px-3 text-xs font-semibold text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]"
              onClick={() => setConfirmVoiceDiscard(false)}
            >
              {t.media.keepVoiceMessage}
            </button>
            <button
              type="button"
              className="min-h-9 rounded-[var(--list-control-radius)] bg-[var(--color-danger)] px-3 text-xs font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]"
              onClick={discardVoiceAndSwitchToInternalNote}
            >
              {t.media.discardVoiceAndSwitch}
            </button>
          </div>
        </div>
      ) : null}
      {attachmentSource && appearance !== 'admin-desk' ? (
        <div className="mb-2">
          <MessagingAttachmentPicker
            appearance="default"
            source={attachmentSource}
            selectedIds={attachmentIds}
            onSelectionChange={setAttachmentIds}
            disabled={otherMediaControlDisabled}
          />
        </div>
      ) : null}
      {attachmentSource ? (
        <div className="mb-2 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {appearance === 'admin-desk' ? (
              <button ref={driveTriggerRef} type="button" aria-label={t.attachments.title} aria-expanded={activePanel === 'drive'} disabled={otherMediaControlDisabled} onClick={() => togglePanel('drive')} className="inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:opacity-40">
                <PexIcon name="link" size="sm" decorative />
              </button>
            ) : null}
            <button
              ref={photoTriggerRef}
              type="button"
              aria-label={t.media.addPhotos}
              aria-expanded={activePanel === 'photo'}
              aria-describedby={selectedVoice ? voiceBlocksOtherId : undefined}
              disabled={otherMediaControlDisabled}
              onClick={() => togglePanel('photo')}
              className={appearance === 'admin-desk' ? 'inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:opacity-40' : 'inline-flex min-h-11 items-center gap-2 rounded-[var(--list-control-radius)] border border-[var(--list-control-border)] bg-[var(--list-control-bg)] px-3 text-xs font-semibold text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40'}
            >
              <PexIcon name="image" size="sm" decorative />
              {appearance === 'admin-desk' ? <span className="sr-only">{t.media.addPhotos}</span> : t.media.addPhotos}
            </button>
            <button
              ref={voiceTriggerRef}
              type="button"
              aria-label={voiceControlLabel}
              aria-expanded={activePanel === 'voice'}
              aria-pressed={voiceControlPressed}
              aria-describedby={
                hasOtherAttachments ? otherBlocksVoiceId : undefined
              }
              disabled={voiceControlDisabled}
              onClick={activateVoice}
              className={appearance === 'admin-desk' ? `inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-control)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:opacity-40 ${voiceControlPressed ? 'bg-[var(--color-danger)] text-white' : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]'}` : `inline-flex min-h-11 items-center gap-2 rounded-[var(--list-control-radius)] border px-3 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-40 ${voiceControlPressed ? 'border-[var(--color-danger)] bg-[var(--color-danger)] text-white' : 'border-[var(--list-control-border)] bg-[var(--list-control-bg)] text-[var(--color-text)]'}`}
            >
              <PexIcon name={voiceControl.phase === 'recording' ? 'stopRecording' : 'microphone'} size="sm" decorative />
              {appearance === 'admin-desk' ? <span className="sr-only">{voiceControlLabel}</span> : voiceControlLabel}
            </button>
          </div>
          {activePanel === 'drive' || activePanel === 'photo'
            ? activeMediaPanel
            : null}
          {voiceRecorderPanel}
        </div>
      ) : null}
      {selectedVoice ? (
        <p id={voiceBlocksOtherId} className="sr-only">
          {t.media.voiceAttachmentLabel}: {selectedVoice.original_filename}
        </p>
      ) : null}
      {hasOtherAttachments ? (
        <p id={otherBlocksVoiceId} className="sr-only">
          {t.attachments.title}: {otherAttachmentLabels.join(', ')}
        </p>
      ) : null}
      <div className={appearance === 'admin-desk' ? 'flex items-end gap-2 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-2 focus-within:shadow-[var(--shadow-focus)]' : 'flex items-end gap-2'}>
        <textarea
          autoFocus={autoFocus}
          aria-label={t.composer.placeholder}
          value={draft}
          rows={2}
          disabled={!canSend || isSubmitting}
          placeholder={t.composer.placeholder}
          className={appearance === 'admin-desk' ? 'min-h-12 flex-1 resize-none border-0 bg-transparent px-1 py-1 text-sm text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-subtle)] disabled:opacity-50' : 'min-h-11 flex-1 resize-none rounded-[var(--list-control-radius)] border border-[var(--list-control-border)] bg-[var(--list-control-bg)] px-3 py-2 text-sm text-[var(--color-text)] outline-none transition focus:border-[var(--color-action)] focus:ring-2 focus:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:bg-[var(--color-surface-muted)]'}
          onChange={(event) =>
            setDraft(surface, conversationId, event.target.value)
          }
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => void submit()}
          aria-label={isSubmitting ? t.composer.sending : t.composer.send}
          className={appearance === 'admin-desk' ? 'inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-text)] text-[var(--color-surface)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:opacity-30' : 'min-h-11 rounded-[var(--list-control-radius)] bg-[var(--color-action)] px-4 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40'}
        >
          {appearance === 'admin-desk' ? <PexIcon name={isSubmitting ? 'refresh' : 'distribution'} size="sm" decorative /> : isSubmitting ? t.composer.sending : t.composer.send}
        </button>
      </div>
    </section>
  )
}
